import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExclamationTriangle } from 'react-icons/fa';

/* ---------------------------------------------------------------------
   StatCard — KPI card with icon, value, label, and trend
   --------------------------------------------------------------------- */
export const StatCard = ({ icon, iconBg, label, value, trend, trendType = 'neutral', loading, suffix }) => {
  if (loading) {
    return (
      <div className="admin-stat">
        <div className="admin-skeleton" style={{ width: 44, height: 44, borderRadius: 12 }} />
        <div className="admin-skeleton" style={{ width: '60%', height: 13 }} />
        <div className="admin-skeleton" style={{ width: '40%', height: 28 }} />
      </div>
    );
  }
  return (
    <div className="admin-stat">
      {icon && (
        <div className="admin-stat-icon" style={{ background: iconBg || 'var(--admin-primary-light)' }}>
          {icon}
        </div>
      )}
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">
        {value}{suffix}
      </div>
      {trend != null && (
        <span className={`admin-stat-trend ${trendType}`}>{trend}</span>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------------
   ChartCard — card wrapper for recharts visualizations
   --------------------------------------------------------------------- */
export const ChartCard = ({ title, subtitle, children, height = 300, action }) => (
  <div className="admin-card" style={{ padding: 20 }}>
    {(title || action) && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 12 }}>
        <div>
          {title && <h3 className="admin-card-title">{title}</h3>}
          {subtitle && <p className="admin-card-subtitle" style={{ marginBottom: 0 }}>{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    <div style={{ width: '100%', height }}>
      {children}
    </div>
  </div>
);

/* ---------------------------------------------------------------------
   Badge — status pill
   --------------------------------------------------------------------- */
const BADGE_MAP = {
  success: 'admin-badge-success',
  warning: 'admin-badge-warning',
  danger: 'admin-badge-danger',
  error: 'admin-badge-danger',
  info: 'admin-badge-info',
  neutral: 'admin-badge-neutral',
};

export const Badge = ({ status = 'neutral', children }) => (
  <span className={`admin-badge ${BADGE_MAP[status] || BADGE_MAP.neutral}`}>{children}</span>
);

/* ---------------------------------------------------------------------
   EmptyState
   --------------------------------------------------------------------- */
export const EmptyState = ({ icon = '📭', title = 'Nothing here yet', text, action }) => (
  <div className="admin-empty">
    <div className="admin-empty-icon">{icon}</div>
    <div className="admin-empty-title">{title}</div>
    {text && <div className="admin-empty-text">{text}</div>}
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

/* ---------------------------------------------------------------------
   LoadingSkeleton — block-level shimmer
   --------------------------------------------------------------------- */
export const LoadingSkeleton = ({ lines = 5, height = 16 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 8 }}>
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        className="admin-skeleton"
        style={{ height, width: `${85 - i * (60 / Math.max(lines, 1))}%` }}
      />
    ))}
  </div>
);

/* ---------------------------------------------------------------------
   Alert
   --------------------------------------------------------------------- */
export const Alert = ({ type = 'error', children, onClose }) => (
  <div className={`admin-alert admin-alert-${type}`}>
    <div style={{ flex: 1 }}>{children}</div>
    {onClose && (
      <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>
        <FaTimes />
      </button>
    )}
  </div>
);

/* ---------------------------------------------------------------------
   Pagination
   --------------------------------------------------------------------- */
export const Pagination = ({ page, totalPages, totalElements, onPageChange, pageSize }) => {
  const from = totalElements === 0 ? 0 : page * (pageSize || 0) + 1;
  const to = Math.min((page + 1) * (pageSize || 0), totalElements);
  return (
    <div className="admin-pagination">
      <span className="admin-pagination-info">
        {totalElements > 0 ? `Showing ${from}–${to} of ${totalElements}` : 'No results'}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="admin-btn admin-btn-outline"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
        >
          <FaChevronLeft style={{ fontSize: 11 }} /> Prev
        </button>
        <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--admin-text-muted)', padding: '0 8px' }}>
          Page {page + 1} of {Math.max(totalPages, 1)}
        </span>
        <button
          type="button"
          className="admin-btn admin-btn-outline"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(page + 1)}
        >
          Next <FaChevronRight style={{ fontSize: 11 }} />
        </button>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------------
   Modal — shared portal-based modal (consolidates createPortal usage)
   --------------------------------------------------------------------- */
export const Modal = ({ open, onClose, title, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 440, md: 600, lg: 820, xl: 1040 };
  const width = widths[size] || widths.md;

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'relative',
          background: 'var(--admin-surface)',
          borderRadius: 16,
          maxWidth: width,
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--admin-shadow-lg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--admin-border)' }}>
          <h3 style={{ margin: 0, fontFamily: 'var(--admin-font-display)', fontSize: 17, fontWeight: 600, color: 'var(--admin-text)' }}>{title}</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-text-muted)', fontSize: 16, padding: 4 }}>
            <FaTimes />
          </button>
        </div>
        <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--admin-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/* ---------------------------------------------------------------------
   ConfirmDialog — confirmation prompt
   --------------------------------------------------------------------- */
export const ConfirmDialog = ({ open, onClose, onConfirm, title = 'Confirm action', message, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" className="admin-btn admin-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className={`admin-btn ${danger ? 'admin-btn-danger' : 'admin-btn-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        {danger && <FaExclamationTriangle style={{ color: 'var(--admin-danger)', fontSize: 22, flexShrink: 0, marginTop: 2 }} />}
        <p style={{ margin: 0, fontSize: 14, color: 'var(--admin-text-muted)', lineHeight: 1.6 }}>{message}</p>
      </div>
    </Modal>
  );
};

/* ---------------------------------------------------------------------
   SectionHeader — page title + subtitle + action slot
   --------------------------------------------------------------------- */
export const SectionHeader = ({ title, subtitle, action }) => (
  <div className="admin-section-header">
    <div>
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {action}
  </div>
);

/* ---------------------------------------------------------------------
   useAsync — small hook for async data fetching with loading/error/refetch
   --------------------------------------------------------------------- */
export const useAsync = (fn, deps = []) => {
  const [state, setState] = React.useState({ data: null, loading: true, error: null });

  const execute = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn();
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      setState({ data: null, loading: false, error });
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    execute().catch(() => {});
  }, [execute]);

  return { ...state, refetch: execute };
};
